export type SessionUser = {
  id?: string;
  nombre?: string;
  rol?: number | string;
  sucursalId?: number;
};

export const getSessionUser = (): SessionUser | null => {
  try {
    const stored = localStorage.getItem("userData");
    if (!stored) return null;
    const parsed = JSON.parse(stored);
    const user = (parsed?.user ?? parsed ?? null) as Record<string, unknown> | null;
    if (!user) return null;
    return {
      id: String(user.id ?? user.Id ?? ""),
      nombre: String(user.nombre ?? user.Nombre ?? ""),
      rol: (user.rol ?? user.Rol) as number | string | undefined,
      sucursalId: Number(user.sucursalId ?? user.SucursalId ?? 0) || undefined,
    };
  } catch {
    return null;
  }
};

const normalizeRol = (rol: unknown): string => {
  if (rol === 0 || rol === "0") return "admin";
  if (rol === 1 || rol === "1") return "operador";
  if (rol === 2 || rol === "2") return "supervisor";
  return String(rol ?? "")
    .trim()
    .toLowerCase();
};

export const isAdmin = (): boolean => normalizeRol(getSessionUser()?.rol) === "admin";

export const isSupervisor = (): boolean =>
  normalizeRol(getSessionUser()?.rol) === "supervisor";

export const isOperador = (): boolean =>
  normalizeRol(getSessionUser()?.rol) === "operador";

/** El portal web es solo para Administrador y Supervisor. El Operador usa la app móvil. */
export const canAccessWebPortal = (): boolean => isAdmin() || isSupervisor();

const PROD_HOSTNAME = "app-etiquetado-envases.azurewebsites.net";

/** Reportes de expedientes: Supervisor y Administrador. Oculto en producción. */
export const canAccessReportes = (): boolean =>
  window.location.hostname !== PROD_HOSTNAME && (isAdmin() || isSupervisor());

/** Configuración Manifold / Línea: el supervisor solo consulta su sucursal. */
export const isManifoldCatalogReadOnly = (): boolean =>
  isSupervisor() && !isAdmin();

export const clearSession = () => {
  localStorage.removeItem("userData");
  localStorage.removeItem("accessToken");
};

export const getSessionSucursalId = (): number | null => {
  const id = Number(getSessionUser()?.sucursalId);
  return Number.isFinite(id) && id > 0 ? id : null;
};

/** Lee la expiración (exp, en segundos) de un JWT. Devuelve null si no se puede leer. */
const getTokenExpiry = (token: string): number | null => {
  try {
    const payload = token.split(".")[1];
    if (!payload) return null;
    const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const json = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + c.charCodeAt(0).toString(16).padStart(2, "0"))
        .join("")
    );
    const exp = Number(JSON.parse(json)?.exp);
    return Number.isFinite(exp) ? exp : null;
  } catch {
    return null;
  }
};

/** Hay usuario y token en storage, y el token no ha expirado. */
export const hasValidSession = (): boolean => {
  try {
    const token = localStorage.getItem("accessToken");
    if (!token || !getSessionUser()) return false;
    const exp = getTokenExpiry(token);
    return exp === null || exp * 1000 > Date.now();
  } catch {
    return false;
  }
};
